export type TextVariant =
  | 'heading'
  | 'subheading'
  | 'title'
  | 'body'
  | 'caption'
  | 'label'
  | 'overline';

/**
 * Per-variant `lineHeight` ratios per ADR-0073. Applied as `Math.round(
 * resolvedFontSize × ratio)` so accessibility text-scaling still works.
 * `body` 1.5 meets WCAG 1.4.12 paragraph spacing.
 */
export const lineHeightRatios: Record<TextVariant, number> = {
  heading: 1.15,
  subheading: 1.20,
  title: 1.25,
  body: 1.50,
  caption: 1.40,
  label: 1.30,
  overline: 1.40,
};

/**
 * Resolved numeric font sizes for each `typeScale` variant. Mirrors the
 * Tamagui `$N` size tokens from `@tamagui/config/v3` so we can compute
 * concrete `lineHeight` numbers without booting Tamagui (which can't
 * load in the Node test env — same constraint that made inputVariants
 * a pure data module).
 */
export const variantFontSizePx: Record<TextVariant, number> = {
  heading: 32,    // $9
  subheading: 24, // $7
  title: 17,      // $5
  body: 15,       // $3
  caption: 13,    // $2
  label: 13,      // $2
  overline: 11,   // $1
};

/** Named type scale — imported by Typography story and Text primitive. */
export const typeScale = {
  heading:    { fontSize: '$9', fontWeight: '700', lineHeight: Math.round(variantFontSizePx.heading * lineHeightRatios.heading) },
  subheading: { fontSize: '$7', fontWeight: '700', lineHeight: Math.round(variantFontSizePx.subheading * lineHeightRatios.subheading) },
  title:      { fontSize: '$5', fontWeight: '600', lineHeight: Math.round(variantFontSizePx.title * lineHeightRatios.title) },
  body:       { fontSize: '$3', fontWeight: '400', lineHeight: Math.round(variantFontSizePx.body * lineHeightRatios.body) },
  caption:    { fontSize: '$2', fontWeight: '400', lineHeight: Math.round(variantFontSizePx.caption * lineHeightRatios.caption) },
  label:      { fontSize: '$2', fontWeight: '600', lineHeight: Math.round(variantFontSizePx.label * lineHeightRatios.label) },
  overline:   { fontSize: '$1', fontWeight: '700', letterSpacing: 2, lineHeight: Math.round(variantFontSizePx.overline * lineHeightRatios.overline) },
} as const;
